// Campo calculado (docs/specs/campo-calculado.md).
//
// Espelho, no front, do contrato que o banco impõe. A fonte da verdade é o SQL:
// `campo_calc_operadores` (operadores por tipo) e `campo_calc_normalizar` (formato da fórmula),
// em supabase/migrations/20261019201000_campo_calculado_motor.sql. Mudou lá, muda aqui — o teste
// ao lado fixa a tabela para a divergência aparecer.

import type { FieldType } from '@/types/fieldRegistry';

export type OperadorCalc =
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between'
  | 'before' | 'after' | 'within_last_days'
  | 'is' | 'is_not' | 'in' | 'not_in'
  | 'has_any' | 'has_none'
  | 'ddd_is' | 'starts_with' | 'ends_with' | 'contains' | 'not_contains'
  | 'is_empty' | 'is_not_empty';

export interface CondicaoCalc {
  /** Caminho no formato do scoring: `lead:phone`, `lead:custom_data:<field_name>`, `deal:amount`... */
  campo: string;
  /** FieldType do campo referenciado. Preenchido pelo banco ao salvar. */
  tipo?: FieldType;
  operador: OperadorCalc;
  valor: unknown;
}

export interface RegraCalc {
  id?: string;
  nome: string;
  ativa?: boolean;
  pontos: number;
  logica?: 'AND' | 'OR';
  condicoes: CondicaoCalc[];
}

export interface FormulaCalc {
  versao?: 1;
  modo?: 'soma';
  valor_inicial?: number;
  minimo?: number | null;
  maximo?: number | null;
  regras: RegraCalc[];
}

/** Resposta de `campo_calc_explicar`. */
export interface ExplicacaoCalc {
  valor: number;
  regras: { id: string; nome: string; pontos: number; aplicou: boolean }[];
  campo: string;
  /** O que está gravado em custom_data agora. Diferente de `valor` = esperando recálculo. */
  gravado: number | null;
}

const VAZIO: OperadorCalc[] = ['is_empty', 'is_not_empty'];

/** Mesma tabela de `campo_calc_operadores` no banco (spec §5.2). */
export function operadoresDoTipo(tipo: FieldType): OperadorCalc[] {
  switch (tipo) {
    case 'number':
    case 'monetary':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...VAZIO];
    case 'date':
      return ['before', 'after', 'within_last_days', ...VAZIO];
    case 'select':
    case 'estado':
      return ['is', 'is_not', 'in', 'not_in', ...VAZIO];
    case 'multi_select':
    case 'labels':
      return ['has_any', 'has_none', ...VAZIO];
    case 'phone':
      return ['ddd_is', 'starts_with', 'contains', ...VAZIO];
    case 'text':
    case 'textarea':
    case 'email_field':
    case 'cpf':
    case 'cep':
    case 'rg':
    case 'autocomplete':
      return ['is', 'is_not', 'contains', 'not_contains', 'starts_with', 'ends_with', ...VAZIO];
    case 'user':
    case 'entity_reference':
      return ['in', 'not_in', ...VAZIO];
    default:
      return [...VAZIO];
  }
}

export const ROTULO_OPERADOR: Record<OperadorCalc, string> = {
  eq: 'é igual a',
  neq: 'é diferente de',
  gt: 'maior que',
  gte: 'maior ou igual a',
  lt: 'menor que',
  lte: 'menor ou igual a',
  between: 'está entre',
  before: 'antes de',
  after: 'depois de',
  within_last_days: 'nos últimos N dias',
  is: 'é',
  is_not: 'não é',
  in: 'é um de',
  not_in: 'não é um de',
  has_any: 'tem algum de',
  has_none: 'não tem nenhum de',
  ddd_is: 'DDD é',
  starts_with: 'começa com',
  ends_with: 'termina com',
  contains: 'contém',
  not_contains: 'não contém',
  is_empty: 'está vazio',
  is_not_empty: 'está preenchido',
};

/** Valor gravado em custom_data → número (o trigger grava número JSON; legado/forjado vira null). */
export function valorCalculado(bruto: unknown): number | null {
  if (typeof bruto === 'number' && Number.isFinite(bruto)) return bruto;
  if (typeof bruto === 'string' && bruto.trim() !== '' && Number.isFinite(Number(bruto))) return Number(bruto);
  return null;
}

/** "+10", "−5", "0" — sinal explícito para a explicação por regra. */
export function formatarPontos(pontos: number): string {
  const n = pontos.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  if (pontos > 0) return `+${n}`;
  if (pontos < 0) return `−${n.replace('-', '')}`;
  return n;
}
